import { useCallback, useEffect, useRef, useState } from "react";
import {
  CountdownApp,
  RecoveryPanel,
  useCapture,
  useFollowerState,
  useShortcuts,
} from "@cuekards/ui";
import type { OpenConstraints } from "@cuekards/ui";
import { looksLikeALink } from "@cuekards/core";
import type { LoadResult } from "@cuekards/core/script-source";
import { captureConstraints, namesADevice } from "@cuekards/core/media-devices";
import type { CaptureKind } from "@cuekards/core/media-devices";
import { buildScriptModel } from "@cuekards/core/script-model";
import {
  DEFAULT_CONSTANT_SPEED_WPM,
  DEFAULT_OVERLAY,
} from "@cuekards/core/overlay-defaults";
import {
  DELIVERABLE_MIME_PREFERENCES,
  IDLE_STATUS,
  RECORDING_PHASE_LABEL,
  canTogglePause,
  isRecordingActive,
  scriptTitle,
  stopIsCancel,
  type RecordingStatus,
} from "@cuekards/core/recording";
import { followVoiceStatus } from "@cuekards/core/model-download/offer";
import { attachAudioTap } from "./audio-tap.js";
import { ControlBar } from "./ControlBar.js";
import { MID_TAKE_REASON } from "./control-bar.js";
import { type DeviceToggle } from "./device-circles.js";
import {
  MIC_OFF_NOTE,
  cameraStage,
  previewRequest,
  roomToggles,
  scriptSummary,
  stageControls,
  startButton,
  type ToggleState,
} from "./green-room.js";
import { GreenRoom } from "./GreenRoom.js";
import { useIdleFade } from "./idle-fade.js";
import { FollowVoice, turnOn } from "./FollowVoice.js";
import { modelCommand, useModelStatus } from "./model-download.js";
import { ModelPanel } from "./ModelPanel.js";
import { useScrollMode } from "./scroll-link.js";
import { PrompterScreen } from "./PrompterScreen.js";
import { refreshAvailability } from "@cuekards/core/refresh";
import { SettingsScreen } from "./SettingsScreen.js";
import { IDLE, afterTheTake, settle, type Stage } from "./stage.js";
import { useTakeGuards } from "./tab-guards.js";
import {
  dismissSavedTake,
  useDismissedTake,
  useTakeNotice,
} from "./take-notice.js";
import { useCameraPermission, useDevices } from "./use-devices.js";
import {
  addressBarFor,
  currentNavigationType,
  isFreshArrival,
  preloadedDoc,
} from "./shared-link.js";

/**
 * The web shell: the screens a tab has, and the camera that outlives all of them.
 *
 * The prompter itself is `OverlayApp` from the shared package, mounted
 * unmodified — the same component the desktop's overlay window renders, drawing
 * itself from the same contract. Since web-ui ticket 08 it is mounted inside
 * {@link PrompterScreen}, which puts the self-view behind it and a top bar above
 * it; that is a layer *around* the shared component and not an edit to it, which
 * is the distinction that whole screen is built on. Everything in this file is
 * the chrome around it, which is web-only for a reason rather than by omission:
 * the desktop's
 * control window is a *second window* beside the overlay, and a tab has one
 * surface, so what a tab needs is a screen you leave rather than a panel you
 * keep beside the prompter. It is deliberately not a fork of `ControlApp` — the
 * moment the two screens want the same thing, the shared package is where it
 * goes.
 *
 * Since web-ui ticket 05 the camera is the thing that does not belong to a
 * screen. `useCapture` is mounted here, above all three, so the preview the green
 * room opens is still open on the other side of the door and a take borrows it
 * rather than reopening the device at "action". It is also why the two device
 * toggles are decided here, once, and handed to both screens: the room draws them
 * over its preview and the prompter draws them on its control bar, and neither
 * holds a copy — so a microphone switched off in one is off in the other and in
 * the take. The bar itself is the prompter's alone since flow ticket 04.
 *
 * There are two ways to give it a script and one channel to give it through: a
 * Google Doc share link, fetched through the same-origin proxy because the
 * export endpoint will not answer a browser, or the script itself. Which source
 * a string reaches is decided from the string, in the core, so that the one
 * remembered value comes back through the source it went out through — see
 * `RoutedScriptSource`.
 */

export function WebApp(): JSX.Element {
  const [stage, setStage] = useState<Stage>(IDLE);
  /** The two boxes, held here rather than in the setup screen so that leaving
   * the prompter comes back to what is loaded rather than to nothing. */
  const [link, setLink] = useState("");
  const [text, setText] = useState("");
  /**
   * Whether there is a script to refresh.
   *
   * Not derived from the boxes: what is loaded and what is typed are different
   * things the moment either is edited. It follows the worker's own
   * `onScriptLoaded` rather than being set beside the call that caused it — the
   * worker holds the one copy of the script, and this is the page reading that
   * fact rather than keeping a second opinion about it. `Clear` is the one place
   * the two are allowed to differ, and it says why.
   */
  const [loaded, setLoaded] = useState(false);
  const [recording, setRecording] = useState<RecordingStatus>(IDLE_STATUS);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string>();
  /** What is loaded, for the line under the script box. Held rather than derived
   * from the boxes: what is typed and what is loaded are different things the
   * moment either is edited. */
  const [script, setScript] = useState<{ name?: string; words: number }>({
    words: 0,
  });
  const [wpm, setWpm] = useState(DEFAULT_CONSTANT_SPEED_WPM);
  /** How much of the self-view the prompter's scrim washes out (web-ui ticket
   * 08). Followed rather than read once — see the effect below. */
  const [backdrop, setBackdrop] = useState(DEFAULT_OVERLAY.backdrop);
  /** Where down the prompter the reading line sits (text-align ticket 01) — the
   * `⋯` row's label and the scrim's band, from one number. Followed off the
   * prompter's own view state; see the effect below for why not the defaults. */
  const [anchor, setAnchor] = useState(DEFAULT_OVERLAY.anchor);
  /**
   * Whether the page is asking for a camera at all.
   *
   * False on a first visit and true from the moment the button is pressed. On
   * every visit after permission is granted it starts true, because the camera
   * coming up silently and immediately is the whole point — see {@link wantsPreview}.
   */
  const [asked, setAsked] = useState(false);
  /** The half-turn of a retry, which has to pass through "wanting nothing" — see
   * {@link retry}. */
  const [retrying, setRetrying] = useState(false);
  const permission = useCameraPermission();
  const devices = useDevices();

  /** Whether a camera is wanted at all: pressed on this visit, or granted on an
   * earlier one — because after the first time it comes up silently, which is the
   * whole point of the gate being a gate rather than a policy. */
  const wantsPreview = asked || permission === "granted";
  /**
   * What a take would open with, or null until anyone has looked.
   *
   * `captureConstraints` over the same `DeviceState` the core worker resolves —
   * **not** a copy of those lines here. The engine lets a take borrow the
   * already-open camera only while the preview's constraints deep-equal the
   * take's, and the take's come from `DeviceController` over that same state.
   * Anything hand-rolled would work until it drifted by one key, and the failure
   * would not be an error: it would be the camera closing and reopening at the
   * end of the countdown, a light blink and a black frame at "action".
   *
   * Null until `enumerated`, which is FR-31 rather than a wrong sentence. The
   * first `DeviceState` a page pulls is the worker's pre-report one — no devices,
   * so no device resolved, so `{video:{},audio:{}}` — and the real one arrives a
   * turn later with an id pinned. Ask for the first and the preview opens on
   * "whatever the system default is"; the report lands and the engine, correctly,
   * closes it and reopens on the named device. Two opens where the invariant says
   * one. Waiting costs one `enumerateDevices`, which the room does on mount
   * anyway.
   */
  const wanted =
    devices.state === null || !devices.state.enumerated
      ? null
      : captureConstraints(devices.state);
  /* A plain expression per render rather than a memo: `useCapture` compares it
   * structurally and returns immediately when it has not moved, so an unchanged
   * request costs one comparison — and a memo keyed on the wrong thing is how you
   * get a camera that ignores a device change, or one that reopens every frame. */
  const previewWanted: OpenConstraints | null = previewRequest({
    wanted,
    wantsPreview,
    retrying,
  });

  /**
   * The camera, the microphone and the recorder (web ticket 11).
   *
   * `useCapture` is `@cuekards/ui`'s and it is the only thing in either host that
   * calls `getUserMedia` or constructs a `MediaRecorder` — which is what makes
   * FR-31's "the microphone is opened exactly once" a property of the code rather
   * than a rule to remember. Mounted here, above the three screens, so that
   * stepping out of the prompter to look at a setting cannot take a rolling take
   * down with it — and, since web-ui ticket 05, so that the preview the green
   * room opened is still open on the other side of the door for a take to borrow.
   *
   * Two things are the host's: the container order, because a tab has no
   * converter and must record the file it is going to hand over (web ticket 01),
   * and the fork, because the desktop taps the microphone on its main thread and
   * a tab must not.
   */
  const preview = useCapture(
    attachAudioTap,
    DELIVERABLE_MIME_PREFERENCES,
    previewWanted,
  );

  /**
   * The three ways a tab loses a take that a window cannot (web ticket 13).
   *
   * Mounted here, above the three screens, for the reason `useCapture` is: a
   * step into the settings must not take the guard on a rolling take with it.
   * Two of the three are invisible when they work — the close guard and the wake
   * lock — and the third has something to say, which is what comes back.
   */
  const guards = useTakeGuards(recording);

  /**
   * Load a reference, and hand back what came of it.
   *
   * The result rather than a flag since flow ticket 03: the green room's Google
   * Doc dialog puts the fetched text *in the box*, which means it needs
   * `LoadResult.text` and not merely to know that a fetch worked. Every other
   * caller reads `ok` and ignores the rest, which is what a boolean gave them.
   */
  const load = useCallback(
    async (reference: string, thenPrompt = false): Promise<LoadResult> => {
      setStage({ kind: "setup", loading: true });
      const result: LoadResult = await window.cuekards.loadScript(reference);
      if (!result.ok) {
        setStage(settle({ kind: "setup", loading: false, error: result.message }));
        return result;
      }
      setRefreshError(undefined);
      // The address bar becomes the shareable thing — but only for a link. A
      // pasted script is not going in a URL, and a stale `?doc=` beside one would
      // hand whoever copied the address a different script from the one on screen.
      rememberInTheAddressBar(
        looksLikeALink(reference) ? reference.trim() : undefined,
      );
      // Whether a finished load leaves the green room is the caller's, and there
      // are two callers who want it (flow ticket 05). A shared link somebody has
      // just arrived at is one — the whole of the ask is "open it and start
      // reading". `START RECORDING` is the other, and it is the room's one action:
      // it hands the box over and opens the prompter, where the take begins when
      // the reader presses record.
      //
      // Everything else only fills the box. Loading used to be unable to leave
      // this screen at all, because there was nothing here worth staying for;
      // there is now, which is why the flag exists rather than the transition
      // being wired into the load.
      setStage(
        settle(thenPrompt ? { kind: "prompting" } : { kind: "setup", loading: false }),
      );
      return result;
    },
    [],
  );

  /**
   * What the page opens with.
   *
   * A document in the URL that somebody has just *arrived* at is a shared link,
   * and the whole of the ask is "open it and start reading" — so it loads and
   * goes straight to the prompter. Everything else only *prefills*: reopening
   * or reloading should pick up where you left off, and it should not put you
   * in front of a full-screen prompter you did not ask for. See
   * {@link isFreshArrival} for why that distinction has to be drawn at all.
   */
  const sharedLoaded = useRef(false);
  useEffect(() => {
    const shared = preloadedDoc(window.location.search);
    if (shared !== undefined) setLink(shared);

    if (shared !== undefined && isFreshArrival(currentNavigationType())) {
      // Once, and only once. Everything else in this effect is idempotent and
      // survives being run twice, which is what development mode does to it; a
      // load is a network request and a screen change, and is not.
      if (!sharedLoaded.current) {
        sharedLoaded.current = true;
        void load(shared, true);
      }
      return;
    }

    let cancelled = false;
    void window.cuekards.getLastDocUrl().then((last) => {
      if (cancelled || !last) return;
      // The remembered value is whatever was loaded, which is a link or a
      // script — and it goes back in the box it came out of.
      const box = looksLikeALink(last) ? setLink : setText;
      box((current) => (current === "" ? last : current));
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // The recording status greys refresh for the duration of a take (FR-17).
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getRecordingStatus().then((status) => {
      if (!cancelled) setRecording(status);
    });
    const off = window.cuekards.onRecordingStatusChanged(setRecording);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  /**
   * What is loaded, for the line under the script box.
   *
   * Pulled and then followed, like everything else here. The name is the one the
   * source knew, falling back to the document's own first line — the two lines
   * `ScriptController.documentTitle` runs on, which is also what the recording is
   * named after, so the room and the file agree about what this take is called.
   *
   * It carries {@link loaded} as well, which is the same fact one field narrower:
   * the worker is announcing what it now holds, and "there is a script that can
   * be refreshed" is exactly that. Setting it beside each call that caused a load
   * instead would be a second copy of a fact the worker already publishes — and
   * this way a script that reached the worker any other way arms the take too,
   * rather than the room having to have been the one to ask.
   */
  useEffect(() => {
    const take = (payload: { text: string; title?: string } | null): void => {
      if (payload !== null) setLoaded(true);
      setScript(
        payload === null
          ? { words: 0 }
          : {
              ...(() => {
                const name = payload.title ?? scriptTitle(payload.text);
                return name === undefined ? {} : { name };
              })(),
              words: buildScriptModel(payload.text).wordCount,
            },
      );
    };
    let cancelled = false;
    void window.cuekards.getScript().then((payload) => {
      if (!cancelled) take(payload);
    });
    const off = window.cuekards.onScriptLoaded(take);
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  /**
   * The reading rate, for the duration estimate beside the word count.
   *
   * Read live rather than assumed. There is exactly one reading rate in this
   * product — the constant speed the prompter scrolls at, which Settings
   * changes — and a second one written into the script card would disagree with
   * it for anybody who moved the slider, in a way nothing on either screen would
   * explain.
   */
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getScrollState().then((state) => {
      if (!cancelled) setWpm(state.wpm);
    });
    const off = window.cuekards.onScrollStateChanged((state) => setWpm(state.wpm));
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  /**
   * The prompter's scrim over the self-view (web-ui ticket 08).
   *
   * Pulled and then followed, like the four above it, and that shape is the
   * whole of "the Backdrop value drives the wash live, with no reload": the
   * slider ticket 09 puts on the settings screen writes the store, the store
   * broadcasts, and the video behind the script gets lighter under the pointer
   * on a screen you are not looking at.
   *
   * It rides `getOverlaySettings` rather than a channel of its own because it
   * *is* an overlay default — it lives in the same blob the font size does, which
   * is what carries it through a settings export and a rig in a URL without
   * either of those being taught about it.
   */
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getOverlaySettings().then((state) => {
      if (!cancelled) setBackdrop(state.defaults.backdrop);
    });
    const off = window.cuekards.onOverlaySettingsChanged((state) =>
      setBackdrop(state.defaults.backdrop),
    );
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  /**
   * Where the reading line sits (text-align ticket 01).
   *
   * Two things on this screen need it: the `⋯` row's label, which is the only
   * place the setting is ever visible, and the scrim's band, which has to sit
   * where the words do.
   *
   * **Off the view state, and not off the defaults beside it.** It is stored in
   * the same blob `backdrop` is, so reading it the same way was there for the
   * taking — but the prompter itself draws from `getOverlayView`, and taking it
   * from the settings here would give one number two routes into one screen. Two
   * routes is how a label ends up naming a stop the script is not on. The
   * backdrop has only one route because it never reaches the prompter's view at
   * all: it is the layer *behind* the prompter, which is this file's.
   */
  useEffect(() => {
    let cancelled = false;
    void window.cuekards.getOverlayView().then((view) => {
      if (!cancelled) setAnchor(view.anchor);
    });
    const off = window.cuekards.onOverlayViewChanged((view) =>
      setAnchor(view.anchor),
    );
    return () => {
      cancelled = true;
      off();
    };
  }, []);

  /**
   * A take opens the prompter (web-ui ticket 05).
   *
   * Here rather than on the record button, because the button is not the only way
   * in: the `R` key and the "start recording" voice command both go straight to
   * the contract, and a screen change wired to one of the three would leave the
   * other two recording a script nobody can see. The phase leaving `idle` is the
   * one fact all three produce.
   */
  useEffect(() => {
    if (recording.phase === "idle") return;
    setStage((current) =>
      current.kind === "setup" ? { kind: "prompting" } : current,
    );
  }, [recording.phase]);

  // And the take's own sentence leaves the room with it — see `afterTheTake`.
  useEffect(() => {
    if (recording.phase !== "idle") return;
    setStage(afterTheTake);
  }, [recording.phase]);

  /**
   * The second half of "Try again", and the reason it is two renders.
   *
   * `CaptureEngine` remembers what the hardware turned down (`#previewRefused`)
   * and ignores a repeat of it — without that, reporting a refusal re-renders the
   * host, the host asks for the same preview again, and the engine opens a device
   * per frame forever. Clearing the want is what forgets the refusal, so a retry
   * has to be `preview(null)` and *then* `preview(constraints)`.
   *
   * Written as a pass through `asked === false` rather than as a call, because
   * the request is a render here: `useCapture` reads it on every render and hands
   * it to an engine that queues in order. A one-render retry would be swallowed
   * in silence — a button that looks like it does nothing at all, which is the
   * easiest thing in this ticket to get wrong.
   *
   * `asked` goes true on the way through, so that a retry from the first-visit
   * gate leaves the room asking rather than back where it started.
   */
  useEffect(() => {
    if (!retrying) return;
    setRetrying(false);
    setAsked(true);
  }, [retrying]);

  /**
   * Allowing it from the address bar is a retry, without anybody pressing
   * anything.
   *
   * The blocked screen says to allow access there and reload, and `onchange`
   * means the room hears about it the moment it happens — but hearing is not
   * enough on its own. The engine is still holding the refusal it remembers, so
   * a permission that turned green over an unchanged request would change
   * nothing at all, and the room would sit on "Camera and microphone are
   * blocked" over a camera it was now allowed to have.
   *
   * On the *transition* rather than on the state, which is the difference between
   * a recovery and a loop: granted-with-an-error is exactly what a retry that
   * failed again looks like, and re-running from that would open a device per
   * render forever.
   */
  const wasPermitted = useRef(permission);
  useEffect(() => {
    const before = wasPermitted.current;
    wasPermitted.current = permission;
    if (permission !== "granted" || before === "granted") return;
    // Look again first: the device list was gathered without permission, so it
    // has no ids in it and no labels on it. Nothing waits for this — the retry
    // below opens on whatever is resolved now and the enumeration lands behind
    // it — but a room that never re-enumerated would keep the camera's name off
    // the stage for the rest of the visit.
    void devices.enumerate();
    // Nothing to forgive on a first grant — the want was never refused, and the
    // pass through "wanting nothing" would close a camera that is coming up.
    if (preview.error === null) return;
    setRetrying(true);
    // `devices.enumerate` is deliberately not a dependency: it is stable, and
    // naming it would only invite somebody to make it unstable.
  }, [permission, preview.error]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    const result = await window.cuekards.refreshScript();
    setRefreshing(false);
    // A failed refresh leaves the loaded script alone — a blip mid-read must
    // not blank the prompter — so all that is left to do is say so.
    setRefreshError(result && !result.ok ? result.message : undefined);
  }, []);

  const canRefresh = refreshAvailability({ loaded, recording, inFlight: refreshing });
  const takeInProgress = isRecordingActive(recording.phase);
  /**
   * Whether the prompter's chrome has been left alone long enough to recede
   * (web-ui ticket 08).
   *
   * Here rather than inside the control bar, which is where it lived while the
   * bar was the only thing that faded. The prompter has a top bar now and the two
   * have to recede together — and two components each running a timer off the
   * same three events would agree almost always, which is a top bar dimming a
   * beat after the bar under it for no reason anybody could see.
   *
   * Called unconditionally with the stage as its argument, because the three
   * screens are early returns below and a hook cannot live inside one. On every
   * screen but the prompter it is `false` and holds no listener at all.
   */
  const idle = useIdleFade(stage.kind === "prompting");

  /** A picture, rather than a stream — see {@link CameraInputs.streaming}. */
  const showing =
    preview.stream !== null &&
    preview.stream.getTracks().some((track) => track.kind === "video");
  /**
   * The two switches, read off the device state rather than held here (web-ui
   * ticket 06).
   *
   * There is deliberately no `useState` behind these buttons. The take is opened
   * a worker away, out of `captureConstraints` over the very state this is read
   * from — so a copy kept in the page would be a second answer to a question that
   * has to have one: a preview and a take that disagree do not merely record the
   * wrong thing, they stop the take borrowing the open camera and close and reopen
   * the hardware at "action". The press goes to the worker, the worker says what
   * is off, and both the preview above and the recorder over there read that.
   *
   * The cost is a round trip inside one tab before the button inverts, which is a
   * `postMessage`. The saving is that there is nothing to keep in step.
   */
  const switchedOff = devices.state?.off ?? [];
  const cameraOff = switchedOff.includes("videoinput");
  const micOff = switchedOff.includes("audioinput");
  const switchOff = (kind: CaptureKind, off: boolean): void =>
    window.cuekards.setDeviceOff(kind, off);

  const cameraState = cameraStage({
    permission,
    asked,
    streaming: showing,
    error: preview.error,
    cameras: devices.cameras,
    cameraOff,
  });
  /**
   * The bar's two circles, decided once and handed to both screens' bars.
   *
   * Both, and not just the room's: a microphone switched off in the room is still
   * off on the other side of the door, and a prompter drawing a dead plain mic
   * glyph over it would be saying the opposite of what is true.
   */
  const toggles = roomToggles({
    stage: cameraState,
    cameraOn: !cameraOff,
    micOn: !micOff,
  });
  /**
   * Refused for the length of a take, with the state still drawn.
   *
   * The footage is already being written, so there is nothing a press could
   * change about it — the engine refuses to drop a track a recorder is holding,
   * correctly, and a button that inverted over a camera still being recorded
   * would be reporting a state it did not produce. Dimmed rather than taken away,
   * because a microphone switched off *before* the take is still off, and a
   * button that went back to its plain glyph for the length of the take would say
   * the opposite of what is true at the moment it matters most.
   */
  const midTake = takeInProgress
    ? { disabled: true, reason: MID_TAKE_REASON }
    : {};
  /** One circle, from the decision plus the press that goes with it. A refusal
   * the room decided — a blocked camera — outranks the mid-take one, because it
   * is the one that describes why there is nothing to press at all. */
  const circle = (
    state: ToggleState | null,
    kind: CaptureKind,
    isOff: boolean,
  ): DeviceToggle | undefined =>
    state === null
      ? undefined
      : {
          on: state.on,
          ...midTake,
          ...(state.refused === undefined
            ? {}
            : { disabled: true, reason: state.refused }),
          onToggle: () => switchOff(kind, !isOff),
        };
  const bar = {
    camera: circle(toggles.camera, "videoinput", cameraOff),
    microphone: circle(toggles.microphone, "audioinput", micOff),
    /* Whether a take would open anything at all — `namesADevice`, which is the
     * same reading `RecordingController.start` refuses on, so a live button over
     * a refused take is not a state these two can get into. Read off the
     * constraints rather than off the two toggles beside it, because the toggles
     * cannot answer it: a machine with no camera hands over no camera toggle,
     * which from the bar looks exactly like a camera nobody switched off. */
    anyDevice: namesADevice(wanted),
    /* Whether the user has switched the microphone off, which Rehearse's own
     * rule needs. Read off the device state for the same reason `anyDevice` is
     * read off the constraints: the toggle beside it cannot answer it. A blocked
     * camera hands `roomToggles` a circle that is `on: false` because there is no
     * permission, which is not the same fact as a switch the user threw — and
     * `MicOffStrip` two lines below reads this one, so a menu row reading the
     * other would be the disagreement this whole object exists to prevent. */
    micOff,
  };

  /** Ask for the camera, on the press the first-visit gate exists to wait for. */
  const grant = (): void => setAsked(true);
  /**
   * Ask again after a refusal.
   *
   * Only `retrying` is set: the pause it produces is what `previewRequest` reads,
   * and it has to be its own fact rather than something derived from the room
   * having stopped asking. On a *granted* permission the room asks anyway — a
   * camera held by another application is exactly that state — so a pause derived
   * from `asked` would never happen and this button would do nothing at all.
   */
  const retry = (): void => setRetrying(true);
  /* There was a `howItWorks` here, opening `/how-it-works` with `window.open` for
   * the `⋯` menu's row — the last route to that page in this product that went
   * through a callback rather than an `<a>`. Flow ticket 07 took the row out, and
   * with it this host's last reason to know the address: every `?` on every
   * screen is `HowItWorksButton`, an ordinary anchor with the `target="_blank"`
   * that keeps a running take, and it needs nothing from here. */
  /**
   * The room's one action (flow ticket 05).
   *
   * Whatever is in the box is the script, so the box is what is handed over —
   * unconditionally, and that is simpler than tracking whether it has changed
   * since the last load: `loadScript` on identical text is a re-parse rather than
   * a fetch, because `RoutedScriptSource` routes a paste to `PastedScriptSource`.
   * (A *link* would route to the Google Docs source and would be a fetch. After
   * flow ticket 03 the box holds script text and links live in the dialog — but a
   * pasted link still reaches here, and a fetch is the correct thing to do with
   * one.)
   *
   * `thenPrompt` rather than a `setStage` of its own: the shared-link arrival
   * already wanted exactly this transition, and a second path to the same stage is
   * how two callers end up disagreeing about what a finished load does. A refusal
   * returns without moving, and `load` has already put the reason in the card's
   * `.web-error` slot, which is where every load failure on that screen lands.
   *
   * **And that is where it stops. It does not start the take.** The prompter's
   * record button does, and it arrives armed. This is a *second* reason to open
   * the prompter and not a replacement for the first: the effect above still opens
   * it when the recording phase leaves `idle`, which is how the `R` key and the
   * spoken "start recording" get there from anywhere.
   */
  const start = (): void => {
    // `startButton`'s own answer rather than a second copy of its rule, so a press
    // that arrived through the `disabled` attribute cannot reach a refusal the
    // screen never showed — **either of them**, which is what start-path ticket 01
    // fixed. This used to pass the literal `false`, so the in-flight refusal was
    // unreachable from here and only one of the two arms was actually shared.
    //
    // **It buys less than it looks like it does, and that is worth saying.** A
    // second *click* was already impossible: React commits `loading: true` before
    // the next event, so the `disabled` attribute stops it a step earlier than
    // this does. Nor does this close the double-press the ticket described — two
    // presses landing inside one task both run the closure from the pre-load
    // render, where `stage.loading` is still false, and a guard reading state
    // cannot see a load React has not committed yet; a ref would be the fix for
    // that, and there is no press that arrives that way to need one. What it buys
    // is the sentence at `green-room.ts`'s `startButton` being true of every arm:
    // a press reaching this handler by any route meets the refusal the screen
    // would have shown.
    //
    // The narrowing is the price of `loading` living on the stage; this handler is
    // only ever reachable from the room, where the stage is a `setup`.
    const loading = stage.kind === "setup" && stage.loading;
    if (startButton({ text, loading }).disabled) return;
    void load(text, true);
  };

  /**
   * Forget the script: on this screen, in the worker, and in storage.
   *
   * The page's half is "I am not recording this one" — the record button dies
   * because `loaded` is the page's own flag. The worker's half is
   * `clearScript` (ADR-0039), which drops the retained copy and the remembered
   * reference. On a pasted script that reference *is* the script, and without
   * this it sat in `localStorage` and came back into the box on the next visit,
   * which is the opposite of what the privacy page tells somebody Clear does.
   *
   * Not awaited: nothing on this screen waits for it, and the answer arrives
   * only after the forgetting write has been posted, so a reload after it has
   * already landed reads an empty box.
   */
  const clear = (): void => {
    void window.cuekards.clearScript();
    setText("");
    setLink("");
    setLoaded(false);
    setScript({ words: 0 });
    setRefreshError(undefined);
    rememberInTheAddressBar(undefined);
    setStage(IDLE);
  };

  /* The one screen you step *out* to, and it carries three companions: a take
   * started at the prompter outlives a step into it, and a countdown or a rolling
   * badge that vanished when you stepped away would leave you with no idea what
   * the camera was doing. That is FR-49, and it is why these lines are not folded
   * in with the prompter's below — the prompter draws its own badge and must not
   * get a second.
   *
   * **It was two screens until flow ticket 02.** How it works was the other, and
   * it is `/how-it-works` in a tab of its own now: the take carries on because
   * nothing about this tab changed, which is the whole of why a link is allowed
   * where a screen was demanded. */
  if (stage.kind === "settings") {
    return (
      <>
        <SettingsScreen
          disabled={takeInProgress}
          /* The same stream the room's stage and the prompter's backdrop are
           * drawing (web-ui ticket 09) — handed down rather than opened there,
           * which is what keeps the device screen's one `getUserMedia` the
           * button that asks for the device *names*. */
          stream={preview.stream}
          showing={showing}
          onBack={() =>
            setStage(stage.from === "prompting" ? { kind: "prompting" } : IDLE)
          }
        />
        <Countdown status={recording} />
        <RollingBanner status={recording} />
        <BackgroundWarning guards={guards} />
      </>
    );
  }

  if (stage.kind === "prompting") {
    return (
      <>
        {/* The prompter, and the frame it now reads over (web-ui ticket 08).
          * `OverlayApp` is mounted inside it, unmodified — everything the screen
          * gained is a layer around the shared component rather than an edit to
          * it, which is what keeps the desktop and the browser gate reading the
          * same script off the same rules. */}
        <PrompterScreen
          stream={preview.stream}
          backdrop={backdrop}
          anchor={anchor}
          idle={idle}
          takeInProgress={takeInProgress}
          onEdit={() => setStage(IDLE)}
          onSettings={() => setStage({ kind: "settings", from: "prompting" })}
        />
        {/* Above the prompter and below the chrome, which is the order the two
          * rules give: the digit has to be the thing you see, and the transport
          * has to stay reachable, because during the countdown stop is the way
          * to change your mind. Over the video too, now — the vignette it draws
          * itself with was always meant for an arbitrary picture behind it, and
          * on this screen it finally has one: you use those three seconds to get
          * back into frame, so the frame has to be there. */}
        <Countdown status={recording} />
        <Shortcuts
          canRefresh={canRefresh.enabled}
          onRefresh={() => void refresh()}
        />
        <ControlBar
          recording={recording}
          loaded={loaded}
          {...bar}
          anchor={anchor}
          availability={canRefresh}
          idle={idle}
          messages={
            <PrompterMessages
              recording={recording}
              error={refreshError}
              micOff={micOff}
            />
          }
          onEdit={() => setStage(IDLE)}
          onSettings={() => setStage({ kind: "settings", from: "prompting" })}
          onRefresh={() => void refresh()}
        />
        <BackgroundWarning guards={guards} />
      </>
    );
  }

  return (
    <>
      <GreenRoom
        link={link}
        text={text}
        loading={stage.loading}
        error={stage.error}
        summary={scriptSummary({ name: script.name, words: script.words, wpm })}
        stage={cameraState}
        preview={preview}
        devices={devices.state}
        /* The same two objects the prompter's bar is given, drawn over the
         * preview instead of on a floating bar (flow ticket 04). Not a second
         * reading of the device state: `roomToggles` decided both once, above.
         *
         * `null` where the room has nothing pressable, which is `stageControls`'
         * answer and not a list of stage names on the other side — see it for the
         * two states the obvious list gets wrong. */
        circles={
          stageControls(toggles)
            ? { camera: bar.camera, microphone: bar.microphone }
            : null
        }
        micOff={micOff}
        onLink={setLink}
        onText={setText}
        onLoad={load}
        onClear={clear}
        onGrant={grant}
        onRetry={retry}
        onStart={start}
        recovery={<RecoveryPanel disabled={takeInProgress} />}
        follow={<FollowVoice />}
        model={<ModelPanel />}
      />
      {/* No countdown here: a take started from this screen replaces it with the
        * prompter before the count begins, so this screen cannot be on the other
        * side of one. */}
      <BackgroundWarning guards={guards} />
    </>
  );
}

/**
 * That a take ran while the tab was behind something else (web ticket 13,
 * story 53).
 *
 * On every screen, because it is a fact about the take rather than about where
 * the user happens to be standing — and it is *dismissable*, unlike the rolling
 * banner beside it, because it describes something that has already happened
 * rather than something that is still true.
 *
 * `role="alert"` rather than `status`: the take may already be over, so this is
 * the one message on screen that has to interrupt whatever was being read.
 *
 * Since web-ui ticket 08 it is a full-width strip hard against the bottom edge,
 * *under* the floating control bar rather than over it. It used to be a rounded
 * panel at a higher `z-index` than the bar, which meant a warning about a take
 * that had already happened could cover the transport for one that still was.
 */
function BackgroundWarning({
  guards,
}: {
  guards: ReturnType<typeof useTakeGuards>;
}): JSX.Element | null {
  if (guards.backgrounded === undefined) return null;
  return (
    <div className="web-background-warning" role="alert">
      <span>{guards.backgrounded}</span>
      <button type="button" onClick={guards.dismiss} title="Dismiss this warning">
        Dismiss
      </button>
    </div>
  );
}

/**
 * The 3-2-1 before capture begins (FR-44), in a tab.
 *
 * {@link CountdownApp} is the desktop's, unmodified — the same component, drawing
 * the same digit out of the same recording status. What differs is only where it
 * is put: on the desktop it owns a transparent full-screen window, and here it is
 * an in-page overlay, which is what the shared stylesheet already describes it as
 * (`position: fixed; inset: 0`) because a full-screen transparent window is one.
 *
 * **Mounted only while the countdown is running.** The component draws nothing
 * outside `counting` on its own account, so this gate is not what keeps the
 * screen clear — it is what makes "costs nothing when not recording" true of more
 * than the pixels: with no take there is no component, no subscription to the
 * recording status and no request to the worker, which is what the desktop gets
 * from not having created the window yet. The pull-then-follow the component does
 * on mount is exactly what makes that safe, and it is there for the desktop's own
 * reason — the first status is broadcast before the countdown is listening.
 *
 * Rendered on the settings screen as well as the prompter, because a take started
 * at the prompter outlives a step into the settings, and a countdown that
 * vanished when you did would leave you with no idea how long you had.
 */
function Countdown({ status }: { status: RecordingStatus }): JSX.Element | null {
  return status.phase === "counting" ? <CountdownApp /> : null;
}

/**
 * That a take is running, on the screens that are not the prompter (FR-49).
 *
 * The prompter draws its own badge, and on the desktop that is the whole answer,
 * because the overlay is a second window and is always there. A tab has one
 * surface: stepping into the settings to change a font size would otherwise take
 * every sign that the camera is rolling off the screen with it, which is exactly
 * the ambiguity FR-49 exists to forbid.
 *
 * It carries the transport as well as a word, because "you are recording" without
 * a way to act on it is half a message — and this is the screen you are on when
 * you have gone looking for something. Pause is here for a reason stop is not:
 * the shortcuts are mounted on the prompter and nowhere else, so a take paused
 * before stepping in here would otherwise have no way back to rolling but the
 * one that ends it.
 */
function RollingBanner({ status }: { status: RecordingStatus }): JSX.Element | null {
  if (!isRecordingActive(status.phase)) return null;
  return (
    <div className={`web-rolling phase-${status.phase}`} role="status">
      <span className="rec-dot" />
      <span>{RECORDING_PHASE_LABEL[status.phase]}</span>
      <PauseButton status={status} />
      <StopButton status={status} />
    </div>
  );
}

/**
 * The keyboard, mounted only where it applies.
 *
 * A component rather than a call in {@link WebApp}, so that the listener goes up
 * when the prompter does and comes down with it — the green room and the settings
 * screen are both full of things to type into and to press, and the defaults are
 * unmodified keys.
 *
 * Refresh is the one command the page supplies rather than the contract, because
 * the page is what knows whether it is allowed: greying the button mid-take
 * (FR-17) and leaving the key live would be the same mistake with a worse
 * failure, since the key is the one reachable from in front of the camera.
 */
function Shortcuts({
  canRefresh,
  onRefresh,
}: {
  canRefresh: boolean;
  onRefresh: () => void;
}): null {
  useShortcuts("web", {
    refresh: () => {
      if (canRefresh) onRefresh();
    },
  });
  return null;
}

/**
 * Put the loaded document in the address bar, so that copying the address
 * shares the script.
 *
 * `replaceState` rather than `pushState`: loading a script is not a place in
 * history, and making it one would turn Back into a control that unloads your
 * script mid-setup.
 */
function rememberInTheAddressBar(doc: string | undefined): void {
  window.history.replaceState(
    null,
    "",
    addressBarFor(window.location.href, doc),
  );
}

/**
 * What a switched-off microphone costs, above the bar it was switched off on
 * (web-ui ticket 06).
 *
 * **The prompter's alone since flow ticket 04**, and it moved here with its one
 * surviving caller. It was on both screens while the strip belonged to the control
 * bar's dock and the bar was on both — and the room says the same sentence now,
 * under its preview, from the same `MIC_OFF_NOTE` constant. Two renderings of one
 * fact, in the two places it is worth saying: this is where a prompter that has
 * stopped following your voice is the thing you are staring at with no
 * explanation for it. The inverted red circle carries the fact on both screens;
 * this carries what it means.
 *
 * `role="status"` rather than `alert`: nothing has gone wrong, and it describes a
 * state the user put the tab in on purpose.
 */
function MicOffStrip({ off }: { off: boolean }): JSX.Element | null {
  if (!off) return null;
  return (
    <p className="web-off-strip" role="status">
      {MIC_OFF_NOTE}
    </p>
  );
}

/**
 * What the prompter has to say, above the control bar (web-ui ticket 03).
 *
 * All that is left of `PrompterChrome`, which was a fixed top-left column of pill
 * buttons at `opacity: 0.25`. Its buttons are the bar now; these three are the
 * things that were never buttons, and they keep their old class names because
 * what they say has not changed — only where it is said.
 *
 * They sit above the bar rather than in a corner of their own, which is where the
 * brief puts every line this screen has to volunteer, and where ticket 06's
 * camera-off and mic-off strips are specified to appear. One dock, one position,
 * one fade rule.
 *
 * **`ModelChip` is not here, and that is a move rather than a deletion.** The
 * speech model's offer belongs on the screen where you decide what to record
 * against — which is the green room (ticket 05) — and a download bar over a
 * running take was only ever there because the prompter was the one screen with
 * chrome on it. The way back to it is the door this screen's top bar and `⋯`
 * menu both carry, which says `BACK TO SCRIPT`.
 */
function PrompterMessages({
  recording,
  error,
  micOff,
}: {
  recording: RecordingStatus;
  error?: string;
  /** Whether the microphone is switched off — the same strip the room draws, on
   * the screen where a prompter that has stopped following your voice is the
   * thing you are looking at. */
  micOff: boolean;
}): JSX.Element {
  return (
    <div className="web-chrome">
      <FollowState recording={recording} />
      {error !== undefined && (
        <span className="web-chrome-error" role="alert">
          {error}
        </span>
      )}
      <MicOffStrip off={micOff} />
      <TakeOutcome status={recording} />
    </div>
  );
}

/**
 * What the follower is doing (web ticket 14), and what to do about it.
 *
 * A phrase rather than a light, because the interesting states are not binary —
 * "Follow my voice ready", "Listening", "Downloading voice recogniser · 42%" and
 * "Follow my voice missing" are different things to do next. Which phrase is
 * `followVoiceStatus`'s decision, from the follower, the model and the mode
 * together: the follower alone could only say "Unavailable" over a model that
 * was one press from arriving.
 *
 * **The one control it carries is that press**, and only between takes. A
 * download is something you start while you are deciding whether to record, and
 * a button appearing under a hand reaching for stop is not. It turns
 * speech-following on as well as fetching the model, which is the same pair the
 * green room's dialog sends — a download started from `constant` would otherwise
 * end on a recogniser nobody is using.
 *
 * The two buttons that used to stand beside it are in the `⋯` menu now, which is
 * the right place for both: rehearsal is something you do *between* takes to check
 * the follower is keeping up, and back-to-top is how you get out of where
 * rehearsing left you. Neither is a thing you reach for with a camera pointed at
 * you, and the four that are have the bar to themselves.
 *
 * The controller's own error is not drawn in full here. In every phase it appears
 * in it is `followerFailureMessage` wrapped around a sentence, and this strip has
 * room for a phrase — so it goes in the `title`, and the menu's rehearse row
 * carries it as the reason that row is dimmed.
 */
function FollowState({ recording }: { recording: RecordingStatus }): JSX.Element {
  const line = followVoiceStatus({
    follower: useFollowerState(),
    model: useModelStatus(),
    mode: useScrollMode(),
  });
  const action = isRecordingActive(recording.phase) ? undefined : line.action;
  return (
    <span className="web-follow-line">
      <span
        className="web-follow-state"
        role="status"
        {...(line.title === undefined ? {} : { title: line.title })}
      >
        {line.says}
      </span>
      {action !== undefined && (
        <button
          type="button"
          className="web-follow-get"
          onClick={() => {
            turnOn();
            // Through `modelCommand` and never the port, so persistent storage is
            // asked for before anything is cached.
            modelCommand(action.command);
          }}
        >
          {action.label}
        </button>
      )}
    </span>
  );
}

/**
 * Pause and resume, on screen (FR-43, web ticket 12).
 *
 * One button and not two, unlike record and stop: the reason those are two is
 * that reaching for stop and getting start would end a take, and here both
 * directions are the same harmless toggle on the same take. It is also the same
 * `togglePauseRecording` the `Space` key sends, which is what keeps a button and
 * a keyboard from disagreeing about whether a recorder is running — the toggle
 * decides from the phase, in the core, and there is only one phase.
 *
 * `canTogglePause` is that same core rule, so the button is live in exactly the
 * states the engine will act on. Greyed rather than removed for the rest of the
 * take, so the control does not appear and disappear under a hand reaching for
 * it mid-take.
 */
function PauseButton({ status }: { status: RecordingStatus }): JSX.Element {
  const paused = status.phase === "paused";
  return (
    <button
      type="button"
      className="web-pause"
      onClick={() => window.cuekards.togglePauseRecording()}
      disabled={!canTogglePause(status.phase)}
      title={
        paused
          ? "Carry on recording into the same file"
          : "Pause — the take carries on in one file when you resume"
      }
    >
      {paused ? "Resume" : "Pause"}
    </button>
  );
}

/**
 * Ending a take, wherever the user is when they decide to.
 *
 * One component and not two identical buttons, because of the two rules inside
 * it: once the phase is `stopping` the file is already being written and there is
 * nothing left for a second stop to do, and during the countdown a stop is a
 * *cancel* — `go` was never sent, so there is no file and nothing to save. Saying
 * "Stop" there would promise one. Two copies of that would eventually be one copy
 * of it.
 */
function StopButton({
  status,
  className,
}: {
  status: RecordingStatus;
  className?: string;
}): JSX.Element {
  const cancel = stopIsCancel(status.phase);
  return (
    <button
      type="button"
      {...(className === undefined ? {} : { className })}
      onClick={() => window.cuekards.stopRecording()}
      disabled={status.phase === "stopping"}
      title={
        cancel
          ? "Change your mind — nothing has been recorded yet"
          : "End the take and save it"
      }
    >
      {cancel ? "Cancel" : "Stop"}
    </button>
  );
}

/**
 * What became of the last take.
 *
 * It is the whole point of the exercise and the one thing the prompter's own
 * badge deliberately does not say: the badge answers "am I rolling", in a corner,
 * from filming distance, and a filename is neither of those. A failure is louder
 * than a success for the obvious reason — a take that did not save is a take
 * still sitting in storage, and the message says where.
 */
function TakeOutcome({ status }: { status: RecordingStatus }): JSX.Element | null {
  const notice = useTakeNotice();
  const dismissed = useDismissedTake();
  /* A take starting clears `lastOutput`, and that is the moment a close is
   * forgotten — so the next take's line is drawn even if it reuses a name. */
  useEffect(() => {
    if (status.lastOutput === undefined) dismissSavedTake(undefined);
  }, [status.lastOutput]);
  if (status.error !== undefined) {
    return (
      <span className="web-chrome-error" role="alert">
        {status.error}
      </span>
    );
  }
  if (status.lastOutput !== undefined && status.lastOutput !== dismissed) {
    const output = status.lastOutput;
    return (
      <span className="web-chrome-saved" role="status">
        <span>
          Saved {output}
          {notice !== undefined && (
            <span className="web-chrome-note"> {notice}</span>
          )}
        </span>
        {/* The way out. It stayed until the next take before this, and the
          * file is already wherever the save dialog put it. */}
        <button
          type="button"
          className="web-chrome-close"
          aria-label="Dismiss"
          title="Dismiss"
          onClick={() => dismissSavedTake(output)}
        >
          ×
        </button>
      </span>
    );
  }
  return null;
}
